import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Check, Lightbulb, BookOpen, ChevronRight } from "lucide-react";
import { getLesson, getLearn } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { StaggerGrid, StaggerItem } from "../components/Motion";
import { DataState } from "../components/DataState";
import { SkeletonBlock } from "../components/LoadingSkeleton";
import { LessonCard } from "../components/learn/LessonCard";

const ORDER = ["how-markets-work", "what-is-a-stock", "research-a-company", "read-financials", "understanding-risk", "valuation-basics"];

export default function Lesson() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setLesson(null); setNotFound(false); setError(false);
    getLesson(slug).then(setLesson).catch((requestError) => {
      if (requestError?.response?.status === 404) setNotFound(true);
      else setError(true);
    });
    getLearn().then((d) => setOthers(d.topics.filter((t) => t.id !== slug))).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop / (h.scrollHeight - h.clientHeight || 1);
      setProgress(Math.min(100, Math.max(0, scrolled * 100)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [lesson]);

  if (notFound) return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-white/[0.07] bg-[#0B0C0E] py-24 text-center">
      <h2 className="text-lg font-semibold text-white">Lesson not found</h2>
      <Link to="/learn" className="mt-4 rounded-md border border-white/10 px-4 py-2 text-sm text-zinc-300 hover:border-white/25">Back to Learn</Link>
    </div>
  );
  if (error) return <DataState title="Lesson temporarily unavailable" detail="LONGVIEW could not retrieve this lesson. Please try again shortly." onRetry={() => window.location.reload()} />;
  if (!lesson) return <LessonSkeleton />;

  const idx = ORDER.indexOf(slug);
  const next = idx >= 0 && idx < ORDER.length - 1 ? ORDER[idx + 1] : null;
  const related = others.slice(0, 3);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* progress bar */}
      <div className="fixed left-0 right-0 top-0 z-[55] h-0.5 bg-transparent">
        <div className="h-full bg-amber-400 transition-[width] duration-150" style={{ width: `${progress}%` }} data-testid="lesson-progress-bar" />
      </div>

      {/* Breadcrumb */}
      <nav data-testid="lesson-breadcrumb" className="flex items-center gap-1.5 font-mono text-xs text-zinc-500">
        <Link to="/learn" className="inline-flex items-center gap-1.5 transition-colors hover:text-zinc-300"><ArrowLeft className="h-3.5 w-3.5" /> Learn</Link>
        <ChevronRight className="h-3 w-3 text-zinc-700" />
        <span className="truncate text-zinc-400">{lesson.title}</span>
      </nav>

      <Reveal>
        <div data-testid="lesson-header">
          <div className="flex items-center gap-3">
            <span className="rounded border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300">{lesson.level}</span>
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-zinc-500"><Clock className="h-3 w-3" />{lesson.minutes} min read</span>
          </div>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">{lesson.title}</h1>
          <p className="mt-4 border-l-2 border-amber-500/40 pl-4 font-serif text-lg italic leading-relaxed text-zinc-300">{lesson.intro}</p>
        </div>
      </Reveal>

      {/* Editorial reading content — flowing sections, not stacked boxes */}
      <div className="divide-y divide-white/[0.06]">
        {lesson.sections.map((s, i) => (
          <Reveal key={i} delay={i * 30}>
            <div className="py-6 first:pt-0">
              <div className="mb-2.5 flex items-center gap-2.5">
                <span className="font-mono text-xs text-amber-400/70">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="text-lg font-semibold text-white">{s.heading}</h2>
              </div>
              <p className="text-[15px] leading-[1.8] text-zinc-300">{s.body}</p>
              {s.example && (
                <div className="mt-4 border-l-2 border-sky-500/30 pl-4">
                  <p className="flex gap-2 text-sm leading-relaxed text-sky-100/80">
                    <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-400" />
                    <span><span className="font-mono text-[10px] uppercase tracking-wider text-sky-400/80">Example · </span>{s.example}</span>
                  </p>
                </div>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      {/* What to remember */}
      <Reveal>
        <div data-testid="lesson-remember" className="lv-panel lv-top-edge p-6" style={{ background: "linear-gradient(180deg, rgba(245,158,11,0.05), transparent)" }}>
          <div className="kicker mb-3 text-amber-400">What to remember</div>
          <ul className="space-y-2.5">
            {lesson.remember.map((k, i) => (
              <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-zinc-200">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />{k}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      {/* Continue Exploring — other real lessons from the existing library */}
      {related.length > 0 && (
        <Reveal>
          <div className="border-t border-white/[0.06] pt-6">
            <div className="kicker mb-3">Continue Exploring</div>
            <StaggerGrid data-testid="lesson-continue-exploring" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {related.map((t) => <StaggerItem key={t.id}><LessonCard t={t} compact testId={`lesson-related-${t.id}`} /></StaggerItem>)}
            </StaggerGrid>
          </div>
        </Reveal>
      )}

      <div className="flex items-center justify-between border-t border-white/[0.06] pt-5">
        <Link to="/learn" className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-4 py-2 text-sm text-zinc-300 transition-colors hover:border-white/25"><BookOpen className="h-4 w-4" /> All lessons</Link>
        {next && (
          <button data-testid="lesson-next" onClick={() => navigate(`/learn/${next}`)} className="lv-press inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-200 transition-colors hover:bg-amber-500/20">
            Next lesson <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

const LessonSkeleton = () => (
  <div className="mx-auto max-w-3xl space-y-6">
    <SkeletonBlock className="h-4 w-32" />
    <SkeletonBlock className="h-32" />
    <SkeletonBlock className="h-96" />
  </div>
);
